import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, InputAdornment, IconButton, useTheme, CircularProgress, Chip, Button } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { useWebSocket } from '../../hooks/useWebSocket';
import SignalWifiStatusbar4BarIcon from '@mui/icons-material/SignalWifiStatusbar4Bar';
import SignalWifiConnectedNoInternet4Icon from '@mui/icons-material/SignalWifiConnectedNoInternet4';
import { useStore } from '../../stores';
import { observer } from 'mobx-react-lite';
import { DriverLocation } from '../../stores/MainStore';



const motoLightSvg = require('../../assets/png/moto1.png');
const motoDarkSvg = require('../../assets/png/moto1.png');

const MAPBOX_TOKEN = 'pk.eyJ1Ijoidml6dGVuMTgiLCJhIjoiY21kZzlmMzJpMDRidjJscTNvMGliZm9ibCJ9.lb-g6158hGESQudbN48bGQ';

mapboxgl.accessToken = MAPBOX_TOKEN;

const MAP_STYLES = {
    light: 'mapbox://styles/mapbox/streets-v12',
    dark: 'mapbox://styles/mapbox/dark-v11'
};

interface DriverMapProps {
    center?: { lat: number; lng: number };
    zoom?: number;
    height?: string;
}

const DriverMap: React.FC<DriverMapProps> = ({
    center = { lat: 10.500, lng: -66.849 },
    zoom = 13,
    height = '100vh',
}) => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const mapStyle = isDarkMode ? MAP_STYLES.dark : MAP_STYLES.light;

    const { mainStore } = useStore();

    const [searchTerm, setSearchTerm] = useState('');
    const [selectedDriver, setSelectedDriver] = useState<DriverLocation | null>(null);
    const [filteredDrivers, setFilteredDrivers] = useState<DriverLocation[]>([]);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [wsConnected, setWsConnected] = useState(false);
    const [driversCount, setDriversCount] = useState(0);
    const [debugMode, setDebugMode] = useState(false);

    const mapContainer = useRef<HTMLDivElement>(null);
    const map = useRef<mapboxgl.Map | null>(null);
    const popup = useRef<mapboxgl.Popup | null>(null);
    const driverMarkersRef = useRef<Map<number, mapboxgl.Marker>>(new Map());
    const markerStyleAddedRef = useRef(false);
    const debugMarkersRef = useRef<mapboxgl.Marker[]>([]);
    const controlsAddedRef = useRef(false);
    const activeTooltipRef = useRef<HTMLElement | null>(null);



    const globalWebSocket = (window as any).globalWebSocket;
    const isConnected = globalWebSocket?.isConnected || false;
    const sendMessage = globalWebSocket?.sendMessage || (() => false);


    useEffect(() => {
        setWsConnected(isConnected);
    }, [isConnected]);


    useEffect(() => {
        if (markerStyleAddedRef.current) return;

        const style = document.createElement('style');
        style.textContent = `
            .driver-marker {
                cursor: pointer;
                width: 1px;
                height: 1px;
                position: relative;
                pointer-events: none;
            }
            .driver-marker-svg {
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: -16px;
                left: -16px;
                transition: transform 0.3s ease;
                pointer-events: auto;
                z-index: 100;
                filter: drop-shadow(0 2px 6px rgba(0,0,0,0.3));
            }
            .driver-marker-svg.online svg {
                filter: hue-rotate(120deg) saturate(1.5) brightness(1.1);
            }
            .driver-marker-svg.offline svg {
                filter: grayscale(0.8) brightness(0.7);
            }
            .driver-marker-arrow {
                width: 0;
                height: 0;
                border-left: 5px solid transparent;
                border-right: 5px solid transparent;
                border-bottom: 8px solid white;
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                transform-origin: center center;
            }
            .driver-marker-tooltip {
                position: absolute;
                bottom: 50px;
                left: 50%;
                transform: translateX(-50%);
                background-color: ${isDarkMode ? 'rgba(33, 33, 33, 0.98)' : 'rgba(255, 255, 255, 0.98)'};
                color: ${isDarkMode ? 'white' : 'black'};
                padding: 12px 14px;
                border-radius: 12px;
                font-size: 12px;
                font-weight: 500;
                white-space: nowrap;
                display: none;
                box-shadow: 0 8px 32px rgba(0,0,0,0.3), 0 2px 8px rgba(0,0,0,0.2);
                border: 2px solid ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'};
                z-index: 99999;
                min-width: 200px;
                text-align: left;
                pointer-events: none;
                backdrop-filter: blur(10px);
                -webkit-backdrop-filter: blur(10px);
            }
            .driver-marker-tooltip-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin: 2px 0;
            }
            .driver-marker-tooltip-name {
                font-weight: 600;
                color: ${theme.palette.primary.main};
                margin-bottom: 4px;
                display: flex;
                align-items: center;
            }
            .driver-marker-tooltip-status {
                width: 8px;
                height: 8px;
                border-radius: 50%;
                margin-right: 6px;
                background-color: ${theme.palette.success.main};
            }
            .driver-marker-tooltip-status.offline {
                background-color: ${theme.palette.grey[500]};
            }
            .driver-marker-tooltip-label {
                
                font-size: 10px;
            }
            .driver-marker-tooltip-value {
                font-weight: 600;
                font-size: 10px;
            }
            .driver-marker.tooltip-open .driver-marker-tooltip {
                display: block;
            }
            .driver-marker:hover .driver-marker-svg {
                transform: scale(1.1);
            }

            .debug-coord {
                width: 2px;
                height: 2px;
                border-radius: 50%;
                background-color: red;
                border: 1px solid yellow;
                position: relative;
                z-index: 5;
            }
        `;
        document.head.appendChild(style);
        markerStyleAddedRef.current = true;

        return () => {
            document.head.removeChild(style);
            markerStyleAddedRef.current = false;
        };
    }, [theme.palette.success.main, theme.palette.grey[500], isDarkMode, theme.palette.primary.main]);


    const isValidCoordinates = (lng: number, lat: number): boolean => {
        return !isNaN(lng) && !isNaN(lat) &&
            lng >= -180 && lng <= 180 &&
            lat >= -90 && lat <= 90;
    };





    const subscribeToAreaBounds = (bounds: mapboxgl.LngLatBounds) => {
        if (!isConnected) return;

        sendMessage({
            type: 'subscribe_area',
            bounds: {
                south: bounds.getSouth(),
                west: bounds.getWest(),
                north: bounds.getNorth(),
                east: bounds.getEast()
            }
        });
    };


    const clearAllMarkers = () => {
        driverMarkersRef.current.forEach(marker => marker.remove());
        driverMarkersRef.current.clear();

        debugMarkersRef.current.forEach(marker => marker.remove());
        debugMarkersRef.current = [];
    };


    const addDebugMarker = (lng: number, lat: number) => {
        if (!map.current) return;

        try {
            const el = document.createElement('div');
            el.className = 'debug-coord';
            el.title = `Coordenada exacta: ${lat.toFixed(8)}, ${lng.toFixed(8)}`;


            const debugMarker = new mapboxgl.Marker({
                element: el,
                anchor: 'center'
            })
                .setLngLat([parseFloat(lng.toFixed(10)), parseFloat(lat.toFixed(10))]);

            if (map.current) {
                debugMarker.addTo(map.current);
                debugMarkersRef.current.push(debugMarker);
            }
        } catch (error) {
            console.error('Error al añadir marcador de depuración:', error);
        }
    };


    const setupDriverMarkers = () => {
        if (!map.current || !mapLoaded) {
            console.warn('Mapa no listo para configurar marcadores');
            return;
        }

        clearAllMarkers();


        if (debugMode && map.current) {
            try {
                const centerMarker = new mapboxgl.Marker({
                    color: '#FF0000',
                    scale: 0.8
                })
                    .setLngLat([center.lng, center.lat]);

                centerMarker.addTo(map.current);
                debugMarkersRef.current.push(centerMarker);
            } catch (error) {
                console.error('Error al añadir marcador central:', error);
            }
        }


        mainStore.driverLocations.forEach(driver => {
            try {
                createDriverMarker(driver);
            } catch (error) {
                console.error(`Error al crear marcador para conductor ${driver.driver_id}:`, error);
            }
        });

    };


    const createDriverMarker = (driver: DriverLocation) => {
        if (!map.current) return;

        const { longitude, latitude, driver_id } = driver;


        if (!isValidCoordinates(longitude, latitude)) {
            console.warn(`No se puede crear marcador con coordenadas inválidas: [${longitude}, ${latitude}]`);
            return;
        }

        try {

            const selectedSvg = isDarkMode ? motoDarkSvg : motoLightSvg;


            const el = document.createElement('div');
            el.className = 'driver-marker';


            el.innerHTML = `
                <div class="driver-marker-svg ${driver.is_online ? 'online' : 'offline'}" style="transform: rotate(${driver.heading}deg)">
                    <img src="${selectedSvg}" alt="Conductor" style="width: 100%; height: 100%; object-fit: contain;" />
                </div>
                <div class="driver-marker-tooltip">
                    <div class="driver-marker-tooltip-name">
                        <span class="driver-marker-tooltip-status ${driver.is_online ? 'online' : 'offline'}"></span>
                        ${driver.name || `Driver ${driver_id}`}
                    </div>
                    <div class="driver-marker-tooltip-row">
                        <span class="driver-marker-tooltip-label">Estado:</span>
                        <span class="driver-marker-tooltip-value">${driver.is_online ? 'Online' : 'Offline'} ${driver.is_available ? '• Disponible' : '• Ocupado'}</span>
                    </div>
                    <div class="driver-marker-tooltip-row">
                        <span class="driver-marker-tooltip-label">Velocidad:</span>
                        <span class="driver-marker-tooltip-value">${Math.round(driver.speed)} km/h</span>
                    </div>
                    <div class="driver-marker-tooltip-row">
                        <span class="driver-marker-tooltip-label">Batería:</span>
                        <span class="driver-marker-tooltip-value">${Math.round(driver.battery_level)}%</span>
                    </div>
                    <div class="driver-marker-tooltip-row">
                        <span class="driver-marker-tooltip-label">Coordenadas:</span>
                        <span class="driver-marker-tooltip-value">${latitude.toFixed(6)}, ${longitude.toFixed(6)}</span>
                    </div>
                </div>
            `;


            const marker = new mapboxgl.Marker({
                element: el,
                anchor: 'center'
            })
                .setLngLat([parseFloat(longitude.toFixed(10)), parseFloat(latitude.toFixed(10))]);


            marker.addTo(map.current);


            driverMarkersRef.current.set(driver_id, marker);


            const svgEl = el.querySelector('.driver-marker-svg');
            if (svgEl) {

                svgEl.addEventListener('click', (e) => {
                    e.stopPropagation();


                    if (activeTooltipRef.current && activeTooltipRef.current !== el) {
                        activeTooltipRef.current.classList.remove('tooltip-open');
                    }


                    const isOpen = el.classList.contains('tooltip-open');
                    if (isOpen) {
                        el.classList.remove('tooltip-open');
                        activeTooltipRef.current = null;
                    } else {
                        el.classList.add('tooltip-open');
                        activeTooltipRef.current = el;
                    }
                });
            }

            if (debugMode) {
                addDebugMarker(longitude, latitude);
            }
        } catch (error) {
            console.error(`Error al crear marcador para conductor ${driver_id}:`, error);


            try {
                if (map.current) {
                    const fallbackMarker = new mapboxgl.Marker({
                        color: driver.is_online ? '#4CAF50' : '#9E9E9E'
                    })
                        .setLngLat([longitude, latitude])
                        .addTo(map.current);

                    driverMarkersRef.current.set(driver_id, fallbackMarker);
                }
            } catch (fallbackError) {
                console.error(`Error al crear marcador básico para conductor ${driver_id}:`, fallbackError);
            }
        }
    };


    const updateDriverMarker = (driver: DriverLocation) => {
        if (!map.current || !mapLoaded) return;

        const { longitude, latitude, driver_id } = driver;


        if (!isValidCoordinates(longitude, latitude)) {
            console.warn(`No se puede actualizar marcador con coordenadas inválidas: [${longitude}, ${latitude}]`);
            return;
        }

        const marker = driverMarkersRef.current.get(driver_id);

        if (marker) {

            marker.setLngLat([parseFloat(longitude.toFixed(10)), parseFloat(latitude.toFixed(10))]);


            const el = marker.getElement();
            if (el.classList.contains('driver-marker')) {
                const svgEl = el.querySelector('.driver-marker-svg');
                if (svgEl) {

                    if (driver.is_online) {
                        svgEl.classList.add('online');
                        svgEl.classList.remove('offline');
                    } else {
                        svgEl.classList.add('offline');
                        svgEl.classList.remove('online');
                    }


                    (svgEl as HTMLElement).style.transform = `rotate(${driver.heading}deg)`;


                    const imgEl = svgEl.querySelector('img');
                    const expectedSvg = isDarkMode ? motoDarkSvg : motoLightSvg;
                    if (imgEl && imgEl.src !== expectedSvg) {
                        imgEl.src = expectedSvg;
                    }
                }


                const tooltipEl = el.querySelector('.driver-marker-tooltip');
                if (tooltipEl) {
                    tooltipEl.innerHTML = `
                        <div class="driver-marker-tooltip-name">
                            <span class="driver-marker-tooltip-status ${driver.is_online ? 'online' : 'offline'}"></span>
                            ${driver.name || `Driver ${driver_id}`}
                        </div>
                        <div class="driver-marker-tooltip-row">
                            <span class="driver-marker-tooltip-label">Estado:</span>
                            <span class="driver-marker-tooltip-value">${driver.is_online ? 'Online' : 'Offline'} ${driver.is_available ? '• Disponible' : '• Ocupado'}</span>
                        </div>
                        <div class="driver-marker-tooltip-row">
                            <span class="driver-marker-tooltip-label">Velocidad:</span>
                            <span class="driver-marker-tooltip-value">${Math.round(driver.speed)} km/h</span>
                        </div>
                        <div class="driver-marker-tooltip-row">
                            <span class="driver-marker-tooltip-label">Batería:</span>
                            <span class="driver-marker-tooltip-value">${Math.round(driver.battery_level)}%</span>
                        </div>
                        <div class="driver-marker-tooltip-row">
                            <span class="driver-marker-tooltip-label">Coordenadas:</span>
                            <span class="driver-marker-tooltip-value">${latitude.toFixed(6)}, ${longitude.toFixed(6)}</span>
                        </div>
                    `;
                }
            }


            if (debugMode) {

                const oldDebugMarkers = debugMarkersRef.current.filter(m => {
                    const lngLat = m.getLngLat();
                    const isCloseEnough = Math.abs(lngLat.lng - longitude) < 0.0001 &&
                        Math.abs(lngLat.lat - latitude) < 0.0001;
                    if (isCloseEnough) {
                        m.remove();
                        return false;
                    }
                    return true;
                });

                debugMarkersRef.current = oldDebugMarkers;
                addDebugMarker(longitude, latitude);
            }
        } else {

            createDriverMarker(driver);
        }
    };


    useEffect(() => {
        if (!mapContainer.current) return;

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: mapStyle,
            center: [center.lng, center.lat],
            zoom: zoom,
            attributionControl: false,

            maxZoom: 22,
            minZoom: 1,
            renderWorldCopies: false,
            preserveDrawingBuffer: true
        });


        mapContainer.current.addEventListener('dblclick', (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                setDebugMode(!debugMode);
            }
        });


        mapContainer.current.addEventListener('click', (e) => {
            if (activeTooltipRef.current) {
                activeTooltipRef.current.classList.remove('tooltip-open');
                activeTooltipRef.current = null;
            }
        });

        map.current.on('load', () => {
            setMapLoaded(true);
        });


        map.current.on('style.load', () => {


            if (map.current) {
                try {

                    if (!controlsAddedRef.current) {
                        map.current.addControl(new mapboxgl.NavigationControl(), 'bottom-right');
                        map.current.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');
                        map.current.addControl(new mapboxgl.GeolocateControl({
                            positionOptions: {
                                enableHighAccuracy: true
                            },
                            trackUserLocation: true
                        }), 'bottom-right');
                        map.current.addControl(new mapboxgl.AttributionControl({ compact: true }), 'bottom-left');


                        controlsAddedRef.current = true;
                    }
                } catch (error) {
                    console.warn('Error al añadir controles:', error);
                }
            }


            setTimeout(() => {
                if (mainStore.driverLocations.length > 0) {
                    setupDriverMarkers();
                }
            }, 100);
        });

        map.current.on('error', (e) => {
            console.error('Error en el mapa:', e);
        });

        map.current.on('moveend', () => {
            if (map.current && isConnected) {
                const bounds = map.current.getBounds();
                if (bounds) {
                    subscribeToAreaBounds(bounds);
                }
            }
        });

        return () => {
            clearAllMarkers();

            controlsAddedRef.current = false;

            activeTooltipRef.current = null;

            if (map.current) {
                map.current.remove();
            }
        };
    }, [mapStyle, debugMode]);


    useEffect(() => {
        if (map.current && mapLoaded) {
            try {

                if (map.current.isStyleLoaded()) {
                    const currentStyle = map.current.getStyle();
                    const currentStyleName = currentStyle?.name || '';


                    if (currentStyleName !== mapStyle) {


                        clearAllMarkers();

                        map.current.setStyle(mapStyle);

                        map.current.once('style.load', () => {
                            setTimeout(() => {
                                if (mainStore.driverLocations.length > 0) {
                                    setupDriverMarkers();
                                }
                            }, 200);
                        });
                    }
                } else {

                    console.log('Estilo aún no cargado, esperando...');
                }
            } catch (error) {
                console.warn('Error al verificar el estilo del mapa:', error);

                if (mainStore.driverLocations.length > 0) {
                    setupDriverMarkers();
                }
            }
        }
    }, [theme.palette.mode, mapLoaded, mapStyle]);


    useEffect(() => {
        if (!mapLoaded || !map.current) return;

        if (debugMode) {

            mainStore.driverLocations.forEach(driver => {
                if (isValidCoordinates(driver.longitude, driver.latitude)) {
                    addDebugMarker(driver.longitude, driver.latitude);
                }
            });
        } else {

            debugMarkersRef.current.forEach(marker => marker.remove());
            debugMarkersRef.current = [];
        }
    }, [debugMode, mapLoaded]);


    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredDrivers(mainStore.driverLocations);
        } else {
            const filtered = mainStore.driverLocations.filter(driver => {
                const driverName = driver.name || `Driver ${driver.driver_id}`;
                return driverName.toLowerCase().includes(searchTerm.toLowerCase());
            });
            setFilteredDrivers(filtered);
        }

        setDriversCount(mainStore.driverLocations.length);
    }, [mainStore.driverLocations, searchTerm]);


    useEffect(() => {
        if (!mapLoaded) return;

        driverMarkersRef.current.forEach((marker, id) => {
            const isVisible = filteredDrivers.some(d => d.driver_id === id);
            const el = marker.getElement();

            if (isVisible) {
                el.style.display = 'flex';
            } else {
                el.style.display = 'none';
            }
        });
    }, [filteredDrivers, mapLoaded]);


    useEffect(() => {
        if (mapLoaded && map.current && mainStore.driverLocations.length > 0) {

            if (map.current.isStyleLoaded()) {
                setupDriverMarkers();
            } else {

                const timeout = setTimeout(() => {
                    if (map.current && map.current.isStyleLoaded()) {
                        setupDriverMarkers();
                    }
                }, 200);

                return () => clearTimeout(timeout);
            }
        }
    }, [mapLoaded, mainStore.driverLocations.length]);


    useEffect(() => {
        if (!mapLoaded || !map.current || !mainStore.driverLocations) return;


        mainStore.driverLocations.forEach(driver => {
            updateDriverMarker(driver);
        });
    }, [mainStore.driverLocations?.length, mapLoaded]);


    const forceRefreshMarkers = () => {
        if (map.current && mapLoaded) {
            clearAllMarkers();
            setupDriverMarkers();
        }
    };


    const handleDriverClick = (driver: DriverLocation) => {
        setSelectedDriver(driver);

        if (map.current) {
            if (popup.current) {
                popup.current.remove();
            }

            const popupStyle = `
                .mapboxgl-popup-content {
                    background-color: ${isDarkMode ? '#333' : '#fff'};
                    color: ${isDarkMode ? '#fff' : '#333'};
                    border-radius: 8px;
                    padding: 12px;
                    box-shadow: 0 2px 10px rgba(0,0,0,${isDarkMode ? '0.5' : '0.2'});
                }
                .mapboxgl-popup-close-button {
                    color: ${isDarkMode ? '#fff' : '#333'};
                    font-size: 16px;
                    padding: 4px 8px;
                }
                .mapboxgl-popup-tip {
                    border-top-color: ${isDarkMode ? '#333' : '#fff'};
                    border-bottom-color: ${isDarkMode ? '#333' : '#fff'};
                }
                .driver-status {
                    display: inline-block;
                    width: 10px;
                    height: 10px;
                    border-radius: 50%;
                    margin-right: 5px;
                }
                .driver-status.online {
                    background-color: ${theme.palette.success.main};
                }
                .driver-status.offline {
                    background-color: ${theme.palette.grey[500]};
                }
                .driver-info-row {
                    margin: 5px 0;
                    display: flex;
                    align-items: center;
                }
            `;

            const popupContent = `
                <style>${popupStyle}</style>
                <div style="padding: 8px;">
                    <div class="driver-info-row">
                        <span class="driver-status ${driver.is_online ? 'online' : 'offline'}"></span>
                        <strong style="color: ${theme.palette.primary.main}">
                            ${driver.name || `Driver ${driver.driver_id}`}
                        </strong>
                    </div>
                    <div class="driver-info-row">
                        <p style="margin: 0; color: ${isDarkMode ? '#ccc' : '#666'};">
                            ${driver.is_online ? 'Online' : 'Offline'} 
                            ${driver.is_available ? '| Disponible' : '| Ocupado'}
                        </p>
                    </div>
                    <div class="driver-info-row">
                        <p style="margin: 0; color: ${isDarkMode ? '#ccc' : '#666'};">
                            Batería: ${Math.round(driver.battery_level)}% | 
                            Velocidad: ${Math.round(driver.speed)} km/h
                        </p>
                    </div>
                    <div class="driver-info-row">
                        <p style="margin: 0; color: ${isDarkMode ? '#ccc' : '#666'};">
                            Ubicación: ${driver.latitude.toFixed(6)}, ${driver.longitude.toFixed(6)}
                        </p>
                    </div>
                </div>
            `;

            popup.current = new mapboxgl.Popup({ closeButton: true })
                .setLngLat([driver.longitude, driver.latitude])
                .setHTML(popupContent)
                .addTo(map.current);

            map.current.flyTo({
                center: [driver.longitude, driver.latitude],
                zoom: 15,
                duration: 2000
            });

            popup.current.on('close', () => {
                setSelectedDriver(null);
            });
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                height,
                overflow: 'hidden',
                backgroundColor: theme.palette.background.default,
            }}
        >
            {!mapLoaded && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.background.default,
                        zIndex: 150,
                    }}
                >
                    <CircularProgress />
                </Box>
            )}

            <Box
                ref={mapContainer}
                sx={{ width: '100%', height: '100%' }}
            />

            <Box
                sx={{
                    position: 'absolute',
                    top: 20,
                    right: 20,
                    left: 20,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                    zIndex: 200,
                    '@media (min-width: 600px)': {
                        flexDirection: 'row',
                        alignItems: 'center',
                    },
                }}
            >
                <TextField
                    fullWidth
                    placeholder="Busca conductores"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton edge="end">
                                    <SearchIcon />
                                </IconButton>
                            </InputAdornment>
                        ),
                        sx: {
                            backgroundColor: theme.palette.background.paper,
                            borderRadius: 2,
                            '& fieldset': {
                                borderRadius: 2,
                                borderColor: theme.palette.divider,
                            },
                            '&:hover fieldset': {
                                borderColor: theme.palette.primary.main,
                            },
                            boxShadow: `0 2px 4px ${theme.palette.mode === 'dark'
                                ? 'rgba(0,0,0,0.5)'
                                : 'rgba(0,0,0,0.1)'}`,
                        },
                    }}
                    sx={{
                        '& .MuiInputBase-input': {
                            color: theme.palette.text.primary,
                        },
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main,
                            },
                        },
                        flex: 1,
                        maxWidth: { xs: '100%', sm: '300px' }
                    }}
                />

                <Chip
                    icon={wsConnected ? <SignalWifiStatusbar4BarIcon /> : <SignalWifiConnectedNoInternet4Icon />}
                    label={wsConnected ? 'WebSocket Conectado' : 'WebSocket Desconectado'}
                    color={wsConnected ? 'success' : 'error'}
                    variant="outlined"
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: `0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)'}`,
                    }}
                />

                <Chip
                    label={`${driversCount} conductores`}
                    color="primary"
                    variant="outlined"
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                        boxShadow: `0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)'}`,
                    }}
                />

                {debugMode && (
                    <>
                        <Chip
                            label="Modo Debug"
                            color="warning"
                            variant="outlined"
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                boxShadow: `0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)'}`,
                            }}
                        />

                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={forceRefreshMarkers}
                            sx={{ display: { xs: 'none', md: 'flex' } }}
                        >
                            Recargar Marcadores
                        </Button>
                    </>
                )}
            </Box>

            {mainStore.lastUpdateTime && (
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: 20,
                        left: 20,
                        backgroundColor: theme.palette.background.paper,
                        padding: '4px 8px',
                        borderRadius: 1,
                        boxShadow: `0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.1)'}`,
                        fontSize: '0.75rem',
                        zIndex: 100,
                    }}
                >
                    Última actualización: {mainStore.lastUpdateTime.toLocaleTimeString()}
                </Box>
            )}
        </Box>
    );
};

export default observer(DriverMap);